import type React from 'react';
import {useState} from 'react';
import {Box, Button, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography} from '@mui/material';
import {Upload} from '@mui/icons-material';
import {formatDate} from '~/components/data-grid/cells/gridCellFormat';
import {Tag} from '~/components/tag/Tag';
import type {MarketActions} from '../../hooks/useMarket';
import {useSourceRegistryQuery} from '../../hooks/useMarket';
import {ProfileSection} from '../components/ProfileSection';
import {UploadSeriesDialog} from './UploadSeriesDialog';

const TIER = {exchange: 'Live (exchange)', official: 'Official statistics', manual: 'Uploaded', licensed: 'Licensed'} as Record<string, string>;
const PROVIDER = {eurostat: 'Eurostat', ecb: 'ECB', databento: 'Databento', manual: 'Uploaded'} as Record<string, string>;
const FRESH = {fresh: {label: 'Fresh', color: 'success'}, stale: {label: 'Stale', color: 'warning'}, empty: {label: 'No data', color: 'default'}} as const;

/** Category source registry: every series this dataset can use, with provider, tier and freshness. */
export const SourceRegistry: React.FC<{databagId: number; actions: MarketActions}> = ({databagId, actions}) => {
    const {data} = useSourceRegistryQuery(databagId);
    const [uploading, setUploading] = useState(false);
    if (!data) return null;

    return (
        <ProfileSection title="Source registry" subtitle="Market data is shared reference data; nothing about you is sent to providers"
                        actions={data.canUpload && <Button size="small" startIcon={<Upload/>} onClick={() => setUploading(true)}>Upload series</Button>}>
            <Box sx={{overflowX: 'auto'}}>
                <Table size="small" aria-label="Source registry">
                    <TableHead>
                        <TableRow>
                            <TableCell>Series</TableCell>
                            <TableCell>Provider</TableCell>
                            <TableCell>Tier</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Latest month</TableCell>
                            <TableCell>Last refresh</TableCell>
                            <TableCell align="right">Used by</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.series.map(s => {
                            const f = FRESH[s.freshness];
                            return (
                                <TableRow key={s.id}>
                                    <TableCell>
                                        <Typography variant="body2">{s.name}</Typography>
                                        <Typography variant="caption" color="text.secondary">{s.unit}</Typography>
                                    </TableCell>
                                    <TableCell>{PROVIDER[s.provider] ?? s.provider}</TableCell>
                                    <TableCell>{TIER[s.tier] ?? s.tier}</TableCell>
                                    <TableCell>
                                        {s.lastError ? (
                                            <Tooltip title={s.lastError}><span><Tag size="small" solid={false} color={s.freshness === 'empty' ? 'default' : 'warning'}
                                                                                    label={s.freshness === 'empty' ? 'Not available' : 'Refresh failed'}/></span></Tooltip>
                                        ) : <Tag size="small" solid={false} color={f.color} label={f.label}/>}
                                    </TableCell>
                                    <TableCell>{s.latestPeriod ? s.latestPeriod.slice(0, 7) : '—'}</TableCell>
                                    <TableCell>{s.lastRefreshAt ? formatDate(s.lastRefreshAt.slice(0, 10)) : '—'}</TableCell>
                                    <TableCell align="right">{s.usedBy ? `${s.usedBy} categor${s.usedBy === 1 ? 'y' : 'ies'}` : '—'}</TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </Box>
            {uploading && <UploadSeriesDialog onUpload={actions.upload} onClose={() => setUploading(false)}/>}
        </ProfileSection>
    );
};
